import React, { useState, useEffect } from 'react';
import './Availability.css';
import { auditFetch } from '../auditFetch';

const API_BASE = 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

export default function Availability() {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const [staff, setStaff] = useState([]);
  const [selectedPersonId, setSelectedPersonId] = useState(null);
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load staff list
  useEffect(() => {
    const loadStaff = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/person`);
        if (res.ok) {
          const data = await res.json();
          setStaff(data);
          // Auto-select first staff if available
          if (data.length > 0 && !selectedPersonId) {
            setSelectedPersonId(data[0].id);
          }
        } else {
          setError('Failed to load staff list');
        }
      } catch (err) {
        setError(`Error loading staff: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadStaff();
  }, [selectedPersonId]);

  // Load availability for selected person
  useEffect(() => {
    const loadAvailability = async () => {
      if (!selectedPersonId) return;

      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/person/${selectedPersonId}/availability`);
        if (res.ok) {
          const data = await res.json();
          const availMap = {};
          data.availability.forEach(item => {
            availMap[item.dayOfWeek] = {
              startTime: item.startTime,
              endTime: item.endTime,
              isAvailable: item.isAvailable
            };
          });
          setAvailability(availMap);
          setError('');
        } else {
          setError('Failed to load availability');
        }
      } catch (err) {
        setError(`Error loading availability: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadAvailability();
  }, [selectedPersonId]);

  const handleTimeChange = (dayOfWeek, field, value) => {
    setAvailability(prev => ({
      ...prev,
      [dayOfWeek]: {
        ...prev[dayOfWeek],
        [field]: value
      }
    }));
  };

  const handleAvailableChange = (dayOfWeek, value) => {
    setAvailability(prev => ({
      ...prev,
      [dayOfWeek]: {
        ...prev[dayOfWeek],
        isAvailable: value
      }
    }));
  };

  const handleSave = async () => {
    if (!selectedPersonId) {
      setError('Please select a person');
      return;
    }

    setSaving(true);
    try {
      const availabilityArray = [];
      for (let i = 0; i < 7; i++) {
        const dayAvail = availability[i] || { startTime: '06:00', endTime: '23:00', isAvailable: true };
        availabilityArray.push({
          dayOfWeek: i,
          startTime: dayAvail.startTime,
          endTime: dayAvail.endTime,
          isAvailable: dayAvail.isAvailable
        });
      }

      const res = await auditFetch(`${API_BASE}/person/${selectedPersonId}/availability`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ availability: availabilityArray })
      });

      if (res.ok) {
        setSuccess('Availability saved successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await res.json();
        setError(`Failed to save: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      setError(`Error saving availability: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const currentStaffName = staff.find(s => s.id === selectedPersonId)?.name || 'Unknown';

  return (
    <div className="availability">
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="availability-container">
        <div className="staff-selector">
          <label htmlFor="staff-select">Select Staff Member:</label>
          <select
            id="staff-select"
            value={selectedPersonId || ''}
            onChange={(e) => setSelectedPersonId(parseInt(e.target.value))}
            disabled={loading}
          >
            <option value="">-- Choose a staff member --</option>
            {staff.map(person => (
              <option key={person.id} value={person.id}>
                {person.name}
              </option>
            ))}
          </select>
        </div>

        {selectedPersonId && (
          <div className="availability-grid">
            <h3>Set Availability for {currentStaffName}</h3>
            <p className="grid-instructions">
              Set your available start and end times for each day of the week.
              Games scheduled within these windows will show you as available.
            </p>

            <table className="availability-table">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Start Time</th>
                  <th>End Time</th>
                  <th>Available</th>
                </tr>
              </thead>
              <tbody>
                {days.map((day, index) => (
                  <tr key={index}>
                    <td className="day-name">{day}</td>
                    <td>
                      <input
                        type="time"
                        value={availability[index]?.startTime || '06:00'}
                        onChange={(e) => handleTimeChange(index, 'startTime', e.target.value)}
                        disabled={loading}
                      />
                    </td>
                    <td>
                      <input
                        type="time"
                        value={availability[index]?.endTime || '23:00'}
                        onChange={(e) => handleTimeChange(index, 'endTime', e.target.value)}
                        disabled={loading}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={availability[index]?.isAvailable !== false}
                        onChange={(e) => handleAvailableChange(index, e.target.checked)}
                        disabled={loading}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="action-buttons">
              <button
                onClick={handleSave}
                disabled={saving || loading}
                className="btn-save"
              >
                {saving ? 'Saving...' : 'Save Availability'}
              </button>
            </div>
          </div>
        )}

        {!selectedPersonId && !loading && (
          <div className="no-selection">
            <p>Please select a staff member to manage their availability</p>
          </div>
        )}
      </div>
    </div>
  );
}
